<template>
  <v-row no-gutters align="center">
    <v-col cols="12">
      <v-row no-gutters align="center">
        <v-col cols="auto" class="pr-2 text-left font-weight-medium">
          {{ $i18n.t('admin.joins.joins') }}
        </v-col>
        <v-col cols="auto" class="pr-2 text-left">
          <base-rounded-button
            icon="mdi-plus"
            size="x-small"
            color="success"
            @click="dialogAction('add')"
            :tooltip="$i18n.t('admin.joins.addJoin')"
          />
        </v-col>
      </v-row>
      <v-row no-gutters class="pt-7" align="center">
        <v-col cols="12">
          <ribbon-list
            :listItems="joinsArray"
            item-title-key="name"
            item-subtitle-key="description"
            no-items-translation-path="admin.joins.noJoins"
            style-type="rawHigher"
            :toggleable="false"
          >
            <template #itemButtons-replace="{ item }">
              <dots-menu :items="joinDotsItems" v-on="getJoinDotsListeners(item)" />
            </template>
          </ribbon-list>
        </v-col>
      </v-row>
    </v-col>
    <main-dialog
      v-if="isMainDialogLoaded"
      :is-visible.sync="isMainDialogVisible"
      v-bind="mainDialogBind"
      @agree="dialogActionHandler"
    />
    <main-input-dialog
      v-if="isMainInputDialogLoaded"
      :is-visible.sync="isMainInputDialogVisible"
      v-bind="mainInputDialogBind"
      @agree="dialogActionHandler"
    >
      <template #main_layer.input="data">
        <v-col cols="6">
          <data-input
            v-bind="data"
            v-model="mainInputDialogInputs.find(input => input.name === 'main_layer').value"
            @input="setCurrentLayerAttributes({ value: $event, type: 'main' })"
          />
        </v-col>
      </template>
      <template #join_layer.input="data">
        <v-col cols="6">
          <data-input
            v-bind="data"
            v-model="mainInputDialogInputs.find(input => input.name === 'join_layer').value"
            @input="setCurrentLayerAttributes({ value: $event, type: 'join' })"
          />
        </v-col>
      </template>
    </main-input-dialog>
  </v-row>
</template>
<script>
import { call, get } from 'vuex-pathify';

import DataInput from '@/components/DataInput';
import DotsMenu from '@/components/DotsMenu';
import RibbonList from '@/components/RibbonList';

import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';

export default {
  name: 'JoinsSettingsList',
  components: {
    DataInput,
    DotsMenu,
    RibbonList,
    MainDialog: () => import('@/components/MainDialog'),
    MainInputDialog: () => import('@/components/MainInputDialog'),
  },
  mixins: [prepareAttributesFeatureCardMixin],
  computed: {
    dataSources: get('layers/metadata'),
    layers: get('layers/layers'),
    vectorLayersNamesArray() {
      return this.$_objectToArray(this.layers || [])
        .filter(layer => {
          return layer.layer_scope === 'core' && layer.type === 'features_layer';
        })
        .map(layer => {
          return { value: layer.id, text: layer.name };
        });
    },

    joins: get('joins/joins'),
    joinsArray() {
      return this.$_objectToArray(this.joins);
    },

    capitalizeCurrentAction() {
      return this.$_capitalize(this.currentAction);
    },

    currentMainLayerRelationAttributes() {
      return this.currentMainLayerAttributes || [];
    },
    currentJoinLayerRelationAttributes() {
      return this.currentJoinLayerAttributes || [];
    },

    currentDialogActionData() {
      return this.dialogsActionsData[this.currentAction] || {};
    },
    dialogsActionsData() {
      return {
        add: {
          agreePath: 'dialog.agreeAdd',
          component: 'MainInputDialog',
        },
        delete: {
          agreePath: 'dialog.agreeDelete',
          component: 'MainDialog',
        },
        edit: {
          agreePath: 'dialog.agreeSave',
          component: 'MainInputDialog',
          initAction: this.setCurrentLayers,
        },
      };
    },
    mainDialogBind() {
      return {
        loading: this.isMainDialogLoading,
        ...(this.isMainDialogVisible
          ? {
              title: this.$i18n.t(`admin.joins.actions.${this.currentAction}JoinTitle`),
              body: this.$i18n.t(`admin.joins.actions.confirmJoin${this.capitalizeCurrentAction}`, {
                name: this.currentActionData.name,
              }),
              agreeText: this.currentDialogActionData.agreePath,
            }
          : {}),
      };
    },
    mainInputDialogBind() {
      return {
        loading: this.isMainInputDialogLoading,
        dialogWidth: '40%',
        ...(this.isMainInputDialogVisible
          ? {
              titleTranslation: this.$i18n.t(`admin.joins.actions.${this.currentAction}JoinTitle`),
              agreeText: this.currentDialogActionData.agreePath,
              inputs: this.mainInputDialogInputs,
            }
          : {}),
      };
    },
    mainInputDialogInputs() {
      const elementEditInputs = [
        {
          name: 'name',
          translationPath: 'admin.joins.attributes.name',
          cols: '12',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: { name: 'text' },
        },
        {
          name: 'description',
          translationPath: 'admin.joins.attributes.description',
          subtitle: this.$i18n.t('default.optional'),
          cols: '12',
          dataType: { name: 'text' },
          clearable: true,
        },
        {
          name: 'main_layer',
          translationPath: 'admin.joins.attributes.mainLayer',
          subtitle: this.$i18n.t('admin.joins.attributes.mainLayerSubtitle'),
          cols: '12',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: { name: 'select' },
          items: this.vectorLayersNamesArray,
        },
        {
          name: 'main_key_attribute',
          translationPath: 'admin.joins.attributes.mainLayerRelationAttribute',
          subtitle: this.$i18n.t('admin.joins.attributes.mainLayerRelationAttributeSubtitle'),
          cols: '12',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: { name: 'select' },
          items: this.currentMainLayerRelationAttributes,
          disabled: !this.currentMainLayerAttributes,
        },
        {
          name: 'join_layer',
          translationPath: 'admin.joins.attributes.joinLayer',
          subtitle: this.$i18n.t('admin.joins.attributes.joinLayerSubtitle'),
          cols: '12',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: { name: 'select' },
          items: this.vectorLayersNamesArray,
        },
        {
          name: 'join_key_attribute',
          translationPath: 'admin.joins.attributes.joinLayerRelationAttribute',
          subtitle: this.$i18n.t('admin.joins.attributes.joinLayerRelationAttributeSubtitle'),
          cols: '12',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: { name: 'select' },
          items: this.currentJoinLayerRelationAttributes,
          disabled: !this.currentJoinLayerAttributes,
        },
      ];
      const mainInputDialogInputsDict = {
        add: elementEditInputs,
        edit: elementEditInputs.map(input => {
          if (input.name === 'main_layer' || input.name === 'join_layer') {
            return { ...input, disabled: true };
          }
          return input;
        }),
      };
      return (mainInputDialogInputsDict[this.currentAction] || []).map(input => {
        return {
          ...input,
          value: JSON.parse(JSON.stringify(this.currentActionData?.[input.name] || null)),
        };
      });
    },
  },
  data: () => ({
    currentAction: '',
    currentActionData: undefined,

    currentMainLayer: '',
    currentMainLayerAttributes: undefined,
    currentJoinLayer: '',
    currentJoinLayerAttributes: undefined,

    joinDotsItems: [{ name: 'edit' }, { name: 'delete' }],

    isMainDialogLoaded: false,
    isMainDialogLoading: false,
    isMainDialogVisible: false,
    isMainInputDialogLoaded: false,
    isMainInputDialogLoading: false,
    isMainInputDialogVisible: false,
  }),
  methods: {
    addJoin: call('joins/addJoin'),
    deleteJoin: call('joins/deleteJoin'),
    editJoin: call('joins/editJoin'),

    getJoinDotsListeners(item) {
      return this.joinDotsItems.reduce((total, action) => {
        total[action.name] = () => {
          this.dialogAction(action.name, item);
        };
        return total;
      }, {});
    },

    dialogAction(type, value) {
      this.currentAction = type;
      this.currentActionData = value;
      const { component, initAction } = this.dialogsActionsData[type];
      if (initAction) initAction({ type, value });
      this[`is${component}Loaded`] = true;
      this[`is${component}Loading`] = false;
      this[`is${component}Visible`] = true;
    },
    async dialogActionHandler(data) {
      const dialogComponent = this.currentDialogActionData?.component;
      this[`is${dialogComponent}Loading`] = true;
      try {
        await this[`handler${this.capitalizeCurrentAction}Join`](data);
        this[`is${dialogComponent}Loading`] = false;
      } catch {
        this[`is${dialogComponent}Loading`] = false;
      }
    },
    async handlerDeleteJoin() {
      await this.deleteJoin(this.currentActionData.id);
      this[`is${this.currentDialogActionData?.component}Visible`] = false;
    },
    async handlerAddJoin() {
      await this.addJoin(this.$_arrayToObject(this.mainInputDialogInputs, 'name', 'value'));
      this[`is${this.currentDialogActionData?.component}Visible`] = false;
    },
    async handlerEditJoin() {
      await this.editJoin({
        id: this.currentActionData.id,
        payload: this.$_arrayToObject(this.mainInputDialogInputs, 'name', 'value'),
      });
      this[`is${this.currentDialogActionData?.component}Visible`] = false;
    },

    setCurrentLayers({ value, types = ['main', 'join'] } = {}) {
      types.forEach(type => {
        this.mainInputDialogInputs[`${this.$_capitalize(type)}_layer`] = value[`${type}_layer`];
        this.setCurrentLayerAttributes({ value: value[`${type}_layer`], type });
      });
    },
    setCurrentLayerAttributes({ value, type = 'main' } = {}) {
      const currentValues = JSON.parse(
        JSON.stringify(this.$_arrayToObject(this.mainInputDialogInputs, 'name', 'value'))
      );
      this[`current${this.$_capitalize(type)}LayerAttributes`] = (
        this.$_flattenStructure(
          this.getForm(
            this.layers[value]?.form_schema?.elements || [],
            this.dataSources[this.layers[value]?.data_source_name]?.attributes_schema?.attributes || []
          )
        ) || []
      ).map(attribute => {
        return { value: attribute.attribute, text: attribute.label, dataType: attribute.dataType };
      });
      setTimeout(() => {
        Object.keys(currentValues)
          .reverse()
          .forEach(key => {
            this.$set(
              this.mainInputDialogInputs.find(input => input.name === key),
              'value',
              currentValues[key]
            );
          });
        this.$forceUpdate();
      });
    },
  },
  mounted() {
    this.$watch(
      vm => [vm.isMainDialogVisible, vm.isMainInputDialogVisible],
      nV => {
        const [isMainDialogVisible, isMainInputDialogVisible] = nV;
        if (!isMainDialogVisible && !isMainInputDialogVisible) {
          this.currentAction = '';
          this.currentActionData = undefined;
          this.currentMainLayerAttributes = undefined;
          this.currentJoinLayerAttributes = undefined;
          this.isMainDialogLoading = false;
          this.isMainInputDialogLoading = false;
        }
      }
    );
  },
};
</script>
